import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, ShieldCheck, RefreshCw } from "lucide-react";

export default function CharacterSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/character/summary").then((r) => setSummary(r.data?.summary || null)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/character/summary");
      setSummary(data.summary);
      toast.success("Sammanställning skapad utifrån dina egna svar");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const sections = (summary?.sections || []).filter((s) => (s.text || "").trim());

  return (
    <Card className="rounded-2xl border-primary/25 bg-primary/[0.03] p-6 md:p-8" data-testid="character-summary-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-head text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Din karaktärssammanställning
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            En varm sammanställning av din <strong>självskattade</strong> karaktärsprofil. Bygger enbart på dina egna svar och exempel – inget hittas på. Detta är din egen beskrivning, inte ett test.
          </p>
        </div>
        <Button className="rounded-full shrink-0" onClick={generate} disabled={loading} data-testid="generate-summary-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : summary ? <RefreshCw className="h-4 w-4 mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {summary ? "Uppdatera sammanställning" : "Skapa min sammanställning"}
        </Button>
      </div>

      {sections.length > 0 ? (
        <div className="space-y-4 stagger-in" data-testid="character-summary-content">
          {sections.map((s) => (
            <div key={s.id} className="rounded-xl bg-white border border-border p-4">
              <div className="text-sm font-semibold mb-1">{s.title}</div>
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{s.text}</p>
            </div>
          ))}
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <ShieldCheck className="h-4 w-4 text-primary" /> Denna profil hålls separerad från ditt CV och dina referenser. Den delas aldrig utan ditt samtycke.
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {summary ? "Fyll i fler exempel ovan för en rikare sammanställning." : "Fyll i dimensionerna ovan och klicka på \u201eSkapa min sammanställning\u201c."}
        </p>
      )}
    </Card>
  );
}
